import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useCountry } from "../../contexts/CountryContext";
import { track } from "../../lib/analytics";
import { Button } from "../ui/button";
import {
  Sparkles, ChevronDown, ChevronUp, ArrowRight, HeartHandshake,
  MessageCircle, RefreshCw, Loader2, Check, ShieldCheck, Compass,
} from "lucide-react";
import { formatPrice } from "./ProductCardV2";

/**
 * Personalised Picks rail — the conversion layer that sits at the very
 * top of /shop. Renders only for signed-in users whose latest
 * assessment yielded one or more support signals.
 *
 * Tone: concierge, not commerce. Never "Buy now" or "Recommended" —
 * always "Most aligned with your goals", "Start here", "Gentle
 * support for…", etc.
 *
 * Includes:
 *   - assessment completion score (nudges if incomplete)
 *   - 1–3 picks with match_percent, match_reasons, why_text
 *   - per-card "Why we suggested this" expandable panel
 *   - footer with mentor / whatsapp / retake assessment CTAs
 *
 * PostHog events:
 *   - personalised_picks_view (mount, once)
 *   - personalised_product_click
 *   - personalised_checkout_click
 *   - mentor_cta_click
 *   - whatsapp_cta_click
 *   - retake_assessment_click
 */
export default function PersonalisedPicks() {
  const { user } = useAuth() || {};
  const ctx = useCountry();
  const country = ctx?.country || "UK";
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!user || !ctx?.hydrated) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    api.get(`/v2/personalised-picks?country=${encodeURIComponent(country)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setState({ loading: false, data, error: null });
        if (data?.picks?.length) {
          try { track?.event?.("personalised_picks_view", { country, picks: data.picks.length, signed_in: true }); } catch {}
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, data: null, error: e?.response?.data?.detail || "Couldn't load picks" });
      });
    return () => { cancelled = true; };
  }, [user, country, ctx?.hydrated]);

  // Anonymous users — show the calm "complete the assessment first" prompt
  if (!user) {
    return <SignedOutPrompt />;
  }

  if (state.loading) {
    return (
      <section className="bg-white border-y border-emerald-100/70" data-testid="personalised-picks-loading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <Loader2 className="w-5 h-5 mx-auto animate-spin text-emerald-700/70" />
          <p className="text-[11px] uppercase tracking-wider text-emerald-700/70 mt-2">Reading your assessment…</p>
        </div>
      </section>
    );
  }

  if (state.data?.requires_assessment) {
    return <AssessmentPrompt firstName={user.name} />;
  }

  const data = state.data;
  if (!data || !data.picks || data.picks.length === 0) return null;

  const firstName = (data.user_name || user.name || "").split(" ")[0] || "";

  return (
    <section
      className="bg-gradient-to-b from-amber-50/40 via-white to-emerald-50/30 border-y border-emerald-100/70"
      data-testid="personalised-picks"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-semibold inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Personalised for {firstName ? firstName : "you"}
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl text-emerald-950 font-light leading-tight mt-1"
              data-testid="personalised-picks-headline"
            >
              Most aligned with your <em className="italic text-emerald-700">goals</em>.
            </motion.h2>
            <p className="text-sm text-emerald-900/75 mt-2 max-w-xl leading-relaxed">
              Based on your assessment, these are the products most aligned with your body, lifestyle and goals — calmly chosen, not algorithmically marketed.
            </p>
          </div>
          {data.completion && (
            <CompletionBadge completion={data.completion} />
          )}
        </div>

        {/* Low-confidence whisper */}
        {data.low_confidence && (
          <div className="mb-5 rounded-2xl border border-amber-200/60 bg-amber-50/40 p-3.5 text-[12px] text-amber-900" data-testid="personalised-picks-low-confidence">
            We don't have a strong enough signal yet — these are calm starting points while we learn more about you. <Link to="/questionnaire?retake=1" className="underline">Add a few more answers</Link> to sharpen the match.
          </div>
        )}

        {/* Picks grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="personalised-picks-grid">
          {data.picks.map((p, idx) => (
            <PickCard
              key={p.id}
              pick={p}
              expanded={!!expanded[p.id]}
              onToggleWhy={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
              firstName={firstName}
              priority={idx === 0}
            />
          ))}
        </div>

        {/* Footer — "Still unsure?" calm CTAs */}
        <StillUnsureBlock />
      </div>
    </section>
  );
}


// ============================================================================
//   Pick card
// ============================================================================
function PickCard({ pick, expanded, onToggleWhy, firstName, priority }) {
  const hero = pick.hero_image?.url || pick.resolved?.hero_image?.url || "";
  const handleProductClick = () => {
    try { track?.event?.("personalised_product_click", { slug: pick.slug, rank: pick.rank, match_percent: pick.match_percent }); } catch {}
  };
  const handlePurchaseClick = () => {
    try { track?.event?.("personalised_checkout_click", { slug: pick.slug, rank: pick.rank, match_percent: pick.match_percent }); } catch {}
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: pick.rank * 0.05 }}
      className={`rounded-3xl bg-white border ${priority ? "border-emerald-300 shadow-lg shadow-emerald-700/8" : "border-emerald-100"} overflow-hidden flex flex-col`}
      data-testid={`personalised-pick-card-${pick.slug}`}
    >
      {/* Image + match badge */}
      <Link to={`/shop/${pick.slug}`} onClick={handleProductClick} className="relative block aspect-[5/4] bg-gradient-to-br from-emerald-50/50 to-amber-50/30 overflow-hidden" data-testid={`personalised-pick-img-${pick.slug}`}>
        {hero ? (
          <img src={hero} alt={pick.name} loading={priority ? "eager" : "lazy"} className="w-full h-full object-contain p-4" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-emerald-700/30"><Sparkles className="w-8 h-8" strokeWidth={1.2} /></div>
        )}
        {/* Match % chip */}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-700 text-white rounded-full px-2.5 py-1 shadow-sm"
          data-testid={`personalised-pick-match-${pick.slug}`}
        >
          <Check className="w-3 h-3" /> {pick.match_percent}% match
        </span>
        {priority && (
          <span className="absolute top-3 right-3 inline-flex items-center text-[10px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-800 rounded-full px-2.5 py-1 border border-amber-200">
            Start here
          </span>
        )}
      </Link>

      <div className="p-5 flex-1 flex flex-col">
        <Link to={`/shop/${pick.slug}`} onClick={handleProductClick} className="font-display text-lg sm:text-xl text-emerald-950 font-light leading-snug hover:text-emerald-700 transition" data-testid={`personalised-pick-name-${pick.slug}`}>
          {pick.name}
        </Link>
        {pick.short_description && (
          <p className="text-[12.5px] text-emerald-900/70 mt-1.5 leading-relaxed line-clamp-2">{pick.short_description}</p>
        )}

        {/* Best-for chips */}
        {(pick.best_for || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {pick.best_for.slice(0, 3).map((b) => (
              <span key={b} className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                {b}
              </span>
            ))}
          </div>
        )}

        {/* "Why we suggested this" toggle */}
        <button
          type="button"
          onClick={onToggleWhy}
          className="mt-4 text-[12px] text-emerald-700 hover:text-emerald-800 inline-flex items-center self-start"
          aria-expanded={expanded}
          data-testid={`personalised-pick-why-toggle-${pick.slug}`}
        >
          Why we suggested this {expanded ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
        </button>
        {expanded && (
          <div className="mt-2 rounded-xl bg-emerald-50/60 border border-emerald-100 p-3 text-[12px] text-emerald-900 leading-relaxed" data-testid={`personalised-pick-why-${pick.slug}`}>
            <p className="font-semibold text-emerald-950">Best for you because…</p>
            <p className="mt-1">{pick.why_text}</p>
            {(pick.match_reasons || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {pick.match_reasons.map((r) => (
                  <span key={r} className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-200">{r.replace(/_/g, " ")}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Price + CTAs */}
        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          <div>
            <p className="font-display text-lg text-emerald-950 leading-none" data-testid={`personalised-pick-price-${pick.slug}`}>
              {formatPrice(pick.price, pick.currency)}
            </p>
            {pick.subscription_price > 0 && pick.subscription_price < pick.price && (
              <p className="text-[10px] text-amber-700 mt-0.5">subscribe {formatPrice(pick.subscription_price, pick.currency)}/mo</p>
            )}
          </div>
          <Link to={`/shop/${pick.slug}`} onClick={handlePurchaseClick} data-testid={`personalised-pick-cta-${pick.slug}`}>
            <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs px-4 py-4">
              Start here <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.article>
  );
}


// ============================================================================
//   Sub-components
// ============================================================================
function CompletionBadge({ completion }) {
  const incomplete = completion.percent < 100;
  if (!incomplete) {
    return (
      <Link
        to="/questionnaire?retake=1"
        className="inline-flex items-center gap-2 text-[12px] text-emerald-700 hover:text-emerald-800"
        onClick={() => { try { track?.event?.("retake_assessment_click", { from: "completion_badge_full" }); } catch {} }}
        data-testid="personalised-picks-completion-full"
      >
        <ShieldCheck className="w-3.5 h-3.5" /> Assessment complete · Retake →
      </Link>
    );
  }
  const missingCount = (completion.missing || []).length;
  return (
    <Link
      to="/questionnaire?retake=1"
      className="rounded-2xl border border-amber-200/60 bg-amber-50/60 p-2.5 hover:bg-amber-50 transition group max-w-[230px]"
      onClick={() => { try { track?.event?.("retake_assessment_click", { from: "completion_badge_partial", percent: completion.percent }); } catch {} }}
      data-testid="personalised-picks-completion-partial"
    >
      <p className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Assessment {completion.percent}%</p>
      <div className="mt-1.5 h-1 w-full bg-amber-100 rounded-full overflow-hidden">
        <div className="h-1 bg-amber-600 rounded-full" style={{ width: `${completion.percent}%` }} />
      </div>
      <p className="text-[11px] text-amber-900 mt-1.5 group-hover:text-amber-800">
        Complete {missingCount} more {missingCount === 1 ? "question" : "questions"} to improve your recommendations →
      </p>
    </Link>
  );
}


function StillUnsureBlock() {
  const onMentor = () => { try { track?.event?.("mentor_cta_click", { from: "personalised_picks" }); } catch {} };
  const onWhatsapp = () => { try { track?.event?.("whatsapp_cta_click", { from: "personalised_picks" }); } catch {} };
  const onRetake = () => { try { track?.event?.("retake_assessment_click", { from: "still_unsure" }); } catch {} };
  return (
    <div className="mt-8 sm:mt-10 rounded-3xl border border-emerald-100 bg-white p-5 sm:p-7 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center" data-testid="personalised-still-unsure">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Still unsure?</p>
        <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light leading-tight mt-1">
          Don't pick alone. We'd rather guide you well.
        </h3>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <Link to="/contact?intent=mentor" onClick={onMentor} data-testid="personalised-unsure-mentor">
          <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 text-xs px-4 py-4">
            <HeartHandshake className="w-3.5 h-3.5 mr-1.5" /> Book a mentor
          </Button>
        </Link>
        <a
          href="https://wa.me/447424225333?text=Hi%2C%20could%20you%20help%20me%20choose%20the%20right%20product%3F"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsapp}
          data-testid="personalised-unsure-whatsapp"
        >
          <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 text-xs px-4 py-4">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Chat on WhatsApp
          </Button>
        </a>
        <Link to="/questionnaire?retake=1" onClick={onRetake} data-testid="personalised-unsure-retake">
          <Button variant="ghost" className="rounded-full text-emerald-900 hover:bg-emerald-50 text-xs px-4 py-4">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retake assessment
          </Button>
        </Link>
      </div>
    </div>
  );
}


function AssessmentPrompt({ firstName }) {
  return (
    <section className="bg-white border-y border-emerald-100" data-testid="personalised-picks-assessment-prompt">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold inline-flex items-center gap-1.5">
            <Compass className="w-3 h-3" /> Personalise your shop
          </p>
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
            {firstName ? `${firstName.split(" ")[0]}, ` : ""}take the free assessment first.
          </h2>
          <p className="text-sm text-emerald-900/75 mt-2 max-w-xl leading-relaxed">
            Two minutes — and we'll show you the wellness picks most aligned with your body and goals.
          </p>
        </div>
        <Link to="/questionnaire" data-testid="personalised-prompt-cta">
          <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-5">
            <Sparkles className="w-4 h-4 mr-1.5" /> Take the assessment
          </Button>
        </Link>
      </div>
    </section>
  );
}


function SignedOutPrompt() {
  return (
    <section className="bg-white border-y border-emerald-100" data-testid="personalised-picks-signed-out">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Want personalised picks?</p>
          <p className="text-sm text-emerald-900/75 mt-1 max-w-xl leading-relaxed">
            Sign in (or take the free assessment) and we'll surface the products most aligned with your goals.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/register" data-testid="personalised-signedout-register">
            <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-4 text-sm">Take free assessment</Button>
          </Link>
          <Link to="/login" data-testid="personalised-signedout-login">
            <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-5 py-4 text-sm">Sign in</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
